import { useRef } from "react";
import { useQuery } from "react-query";

import axios from "axios";

import { PdenDataSourceSetting } from "models/pdenDataSourceSetting";

const chartServiceApiRoot = process.env.REACT_APP_CHART_SERVICE;

export const PRODUCTION_VOLUMES_QUERY_KEY = "productionVolumes";

const PAGE_CACHE_MS = 60 * 1000;
const PAGE_STALE_MS = 5 * 60 * 1000;

export interface ProductionVolumesColumn {
  key: string;
  name: string;
}

// c2-c5 are null for US wells and for Canadian wells with no plant liquids data.
export type ProductionVolumesRow = Record<string, string | number | null>;

export interface ProductionVolumesResponse {
  title: string;
  columns: ProductionVolumesColumn[];
  rows: ProductionVolumesRow[];
  // The data source the service applied, for example "Public".
  sourceSetting: string;
  totalWells: number;
  page: number;
  pageSize: number;
}

export interface ProductionVolumesFilter {
  filterId: { id: string; lastUpdated: number };
  // The app-level Data Source toggle, as charts send it; never the Data Table's own settings.
  pdenDataSourceSetting?: Pick<PdenDataSourceSetting, "source">;
  syncClientPden?: boolean;
  syncClientSchema?: string;
}

export interface ProductionVolumesPageParams extends ProductionVolumesFilter {
  page: number;
  pageSize: number;
}

// The service takes the id only; lastUpdated exists to refresh the cache key.
export const toProductionVolumesBody = ({
  filterId,
  ...rest
}: ProductionVolumesFilter) => ({ filterId: filterId.id, ...rest });

export default function useProductionVolumesQuery(
  params: ProductionVolumesPageParams,
  enabled = true
) {
  const query = useQuery<ProductionVolumesResponse, Error>(
    [PRODUCTION_VOLUMES_QUERY_KEY, params],
    async () => {
      const { data } = await axios.post<ProductionVolumesResponse>(
        `${chartServiceApiRoot}/productionVolumes`,
        toProductionVolumesBody(params)
      );
      return data;
    },
    {
      enabled: enabled && !!params.filterId.id,
      keepPreviousData: true,
      // A page holds up to thousands of wells' months, so a page left behind is dropped
      // after a minute; switching back to Monthly within that minute reuses it.
      cacheTime: PAGE_CACHE_MS,
      staleTime: PAGE_STALE_MS,
      retry: 1,
      refetchOnWindowFocus: false
    }
  );

  // keepPreviousData smooths page changes, but it also serves the last response
  // after the filter or sync settings change, where its rows and totalWells are wrong.
  const scopeKey = JSON.stringify({ ...params, page: 0, pageSize: 0 });
  const settledScopeKey = useRef<string>();
  if (query.isSuccess && !query.isPreviousData) settledScopeKey.current = scopeKey;

  if (query.isPreviousData && settledScopeKey.current !== scopeKey) {
    return { ...query, data: undefined, isLoading: query.isFetching };
  }
  return query;
}
